import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BuzzLogo from "../components/BuzzLogo";
import LiveTicker, { LiveDot } from "../components/LiveTicker";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); nav("/dashboard"); toast.success("Welcome back"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] grid md:grid-cols-2 relative overflow-hidden font-body">
      {/* LEFT — brand rail */}
      <div className="hidden md:flex bg-[#0E1512] text-white p-10 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full bg-[#F5C543]/12 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 hex-bg pointer-events-none" style={{opacity:0.06}} />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><BuzzLogo size={22} /></div>
          <span className="font-heading font-extrabold text-xl">BuzzerAI</span>
        </Link>
        <div className="relative">
          <h2 className="font-heading text-4xl lg:text-5xl leading-[0.95] tracking-tight mt-2">
            The <span className="honey-bar italic text-[#F5C543]">buzz</span> is<br/>still answering.
          </h2>
          <p className="text-sm text-[#C2D1C1] mt-4 max-w-sm leading-snug">
            Log back in to your dashboard. See every call Mimi caught tonight. Drop names from the waitlist as tables clear.
          </p>
          <div className="mt-6 space-y-1.5">
            {["Waitlist stays free forever", "Cancel in one tap · no exit fee", "Founder replies to every email"].map((s) => (
              <div key={s} className="flex items-center gap-2 text-[11px] font-mono text-[#C2D1C1]">
                <CheckCircle size={12} color="#F5C543" weight="fill" /> {s}
              </div>
            ))}
          </div>
        </div>
        <div className="text-[10px] font-mono tracking-widest uppercase text-white/40 relative">
          From $29/mo · no contract · cancel in one tap
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center"><BuzzLogo size={20} /></div>
            <span className="font-heading font-extrabold">BuzzerAI</span>
          </Link>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2 flex items-center gap-2">
            <LiveDot color="#D96C5B" /> Welcome back
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0E1512] tracking-tight leading-none mb-3">Log in.</h1>
          <p className="text-sm text-[#5C6B61] mb-8">Pick up where you left off.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline block mb-1.5">Email</label>
              <input data-testid="login-email" className="input-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="overline">Password</label>
                <Link to="/forgot" className="text-[11px] text-[#5C6B61] hover:text-[#0E1512] underline decoration-dotted underline-offset-2" data-testid="forgot-link">
                  Forgot?
                </Link>
              </div>
              <input data-testid="login-password" className="input-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button data-testid="login-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-6 py-3.5 rounded-full hover:bg-[#1E3A2F] transition-all disabled:opacity-60">
              {busy ? "Signing in…" : "Log in"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>
          <p className="mt-6 text-sm text-[#5C6B61]">
            No account? <Link to="/signup" className="text-[#121914] font-semibold underline decoration-[#F5C543] decoration-2 underline-offset-4">Start free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
